import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Building2, ChevronDown, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useBrandFilter } from '@/components/brand-filter/brand-filter-context';

/**
 * Header multi-select brand filter for aggregate sessions. Built from the shared
 * Popover/Checkbox/Input primitives (mirroring the sound-settings popover)
 * rather than pulling in a command-palette dependency for a list of 10–50 names.
 */
export function BrandFilterPopover() {
  const { t } = useTranslation();
  const { brands, selectedBrandIds, selectedBrands, setSelectedBrandIds, selectAll, selectOnly } = useBrandFilter();
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return brands;
    return brands.filter((brand) => brand.name.toLowerCase().includes(needle));
  }, [brands, query]);

  const label =
    selectedBrandIds === null
      ? t('aggregate.filter.allBrands')
      : selectedBrands.length === 1
        ? selectedBrands[0].name
        : t('aggregate.filter.brandCount', { count: selectedBrands.length });

  const toggle = (brandId: string, checked: boolean) => {
    const current = selectedBrandIds ?? brands.map((brand) => brand.id);
    setSelectedBrandIds(checked ? [...current, brandId] : current.filter((id) => id !== brandId));
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          aria-label={t('aggregate.filter.aria')}
          className="h-8 gap-1.5 px-2 sm:px-2.5 max-w-[200px]"
        >
          <Building2 className="h-4 w-4 shrink-0 text-muted-foreground" />
          <span className="truncate text-xs font-medium">{label}</span>
          <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        </Button>
      </PopoverTrigger>
      {/* Bounded height + flex column so the list below gets a definite height to scroll
          inside (a bare max-h on ScrollArea doesn't clip — it overflows past the popover).
          The cap also tracks the viewport so the list stays inside the screen on mobile. */}
      <PopoverContent
        align="start"
        className="flex max-h-[min(24rem,calc(var(--radix-popover-content-available-height)_-_0.5rem))] w-72 flex-col overflow-hidden p-0"
      >
        <div className="relative shrink-0 border-b border-border p-2">
          <Search className="pointer-events-none absolute left-4 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={t('aggregate.filter.searchPlaceholder')}
            aria-label={t('aggregate.filter.searchPlaceholder')}
            className="h-8 pl-7 text-sm"
          />
        </div>

        <button
          type="button"
          onClick={selectAll}
          className="flex w-full shrink-0 items-center gap-2 border-b border-border px-3 py-2 text-left text-sm hover:bg-muted"
        >
          <Checkbox checked={selectedBrandIds === null} tabIndex={-1} className="pointer-events-none" />
          <span className="font-medium">{t('aggregate.filter.allBrands')}</span>
        </button>

        {/* Plain scroll container rather than ScrollArea: the Radix viewport sizes itself with
            height:100%, which doesn't resolve against a max-height-only parent, so the list would
            be clipped without ever becoming scrollable. */}
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
          <div className="p-1">
            {filtered.length === 0 && (
              <p className="px-2 py-4 text-center text-xs text-muted-foreground">{t('aggregate.filter.noResults')}</p>
            )}
            {filtered.map((brand) => {
              const checked = selectedBrandIds === null || selectedBrandIds.includes(brand.id);
              return (
                <div key={brand.id} className="group flex items-center gap-2 rounded-sm px-2 py-1.5 hover:bg-muted">
                  <Checkbox
                    id={`brand-filter-${brand.id}`}
                    checked={checked}
                    onCheckedChange={(value) => toggle(brand.id, value === true)}
                  />
                  <label
                    htmlFor={`brand-filter-${brand.id}`}
                    className="min-w-0 flex-1 cursor-pointer truncate text-sm"
                    title={brand.name}
                  >
                    {brand.name}
                  </label>
                  <button
                    type="button"
                    onClick={() => selectOnly(brand.id)}
                    className="shrink-0 rounded px-1.5 py-0.5 text-[11px] font-medium text-muted-foreground opacity-0 transition-opacity hover:text-foreground focus-visible:opacity-100 group-hover:opacity-100"
                  >
                    {t('aggregate.filter.only')}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
